import { UI_MAP_STATUS } from "./constant";



export const createInitialEngine = () => ({
  intent: null,
  flow: {
    stepOrder:      [], // Populated after intent is selected
    currentStep:    1,
    completedSteps: [],
  },
  steps: {}, // { [stepId]: StepState } — populated alongside stepOrder
});


const createInitialWorkspace = () => ({
  id:   null,
  name: '',
  role: '',
});


const createInitialProject = () => ({
  id:              null,
  name:            '',
  applicationType: '',
  environment:     '',
  baseUrl:         '',
  apiBaseUrl:      '',
  appPackage:      '',
  skip_bitbucket_flow: false,
});


const createInitialTestSource = () => ({
  type:        null,
  files:       [],
  content:     '',
  gitRepo:     '',
  gitToken:    '',
  framework:   '',
  language:    '',
  jiraConfig:  null,
  jiraProject: '',
  jiraSprint:  '',
});


const createInitialUiMap = () => ({
  status:      UI_MAP_STATUS.NOT_STARTED,
  elements:    [],
  flows:       [],
  lastUpdated: null,
});


const createInitialRecorder = () => ({
  agentConnected:     false,
  extensionConnected: false,
  recordingSessionId: null,
  recordingStatus:    null,
  capturedSteps:      [],
  savedScenarios:     [],
  projectUrl:         '',
  testCaseName:       '',
  description:        '',
  priority:           'medium',
});


const createInitialUiDiscovery = () => ({
  discoveryJobId: null,
  runId:          null,
  status:         null,
  progress:       0,
  baseUrl:        '',
  credentials:    null,
});


const createInitialIot = () => ({
  // Step 5 — Interaction Mode
  interactionMode: null, // PROTOCOL | PHYSICAL | HYBRID

  // Step 6 — Lab Agent
  labAgent: {
    connected:  false,
    agentId:    null,
    deviceName: null,
    version:    null,
    lastPing:   null,
  },

  // Step 7 — Device
  device: {
    deviceId:     null,
    category:     null, // smart_lock | sensor | switch | camera | controller
    connectivity: null, // wifi | ble | zigbee | ethernet | lora
    profile:      null,
    online:       false,
  },

  // Step 8 — Setup
  setup: {
    commands:           [], // PROTOCOL mode
    validations:        [], // PROTOCOL mode
    calibrationProfile: null, // PHYSICAL mode
    actuatorConfig:     null, // PHYSICAL mode
  },

  // Step 9 — Test Cases
  testCaseIds:       [],
  recordingSessionId: null,
  steps: [], 

  // Step 10 — Evidence Collection
  evidence: {
    captureLogs:      true,
    captureTelemetry: true,
    captureVideo:     false,
    captureSnapshots: true,
  },

  // Step 11 — Automation Generation
  automationJob: {
    jobId:    null,
    status:   null,
    progress: 0,
    suiteId:  null,
  },

  // Step 12 — Run
  testRun: {
    runId:   null,
    status:  null,
    logs:    [],
    results: null,
  },
});


const createInitialMigrate = () => ({
  // Step M3 — Project Setup
  projectName: '',
  framework:   null, // selenium | cypress | playwright | robot
  language:    null, // auto-detected or user-specified
  repoType:    'tests_only', // tests_only | full_repo

  // Step M4 — Import
  importJobId:       null,
  importStatus:      null,
  sourceType:        null, // ZIP | GIT | URL
  fileId:            null,
  repoUrl:           null,
  branch:            'main',
  frameworkDetected: null,
  languageDetected:  null,
  testCount:         0,
  runnerDetected:    null,
  dependencies:      [],

  // Step M5 — Conversion Settings
  runMode:            'as_is',
  wrapperEnabled:     true,
  autoDeps:           true,
  envVars:            {},
  convertToPlaywright: false,

  // Step M6 — Convert Progress
  convertJobId:     null,
  convertStatus:    null,
  convertProgress:  0,
  stages:           [],
  runnableSuiteId:  null,
  importedTestIds:  [],
});

/**
 * Creates the initial automation preferences.
 * Applies to the test-generation phase; sensible defaults are pre-filled.
 *
 * @returns {object}
 */
const createInitialAutomationPrefs = () => ({
  browsers:            ['chrome'],
  headless:            false,
  parallel:            false,
  workers:             1,
  testPack:            'smoke',
  locatorStrategy:     'auto',
  retryFlaky:          1,
  defaultTimeout:      30,
  screenshotOnFailure: true,
});

/**
 * Creates the initial generation-job tracking state.
 * Polled after the user triggers test-script generation.
 *
 * @returns {{ jobId: null, status: null, progress: number, logs: [] }}
 */
const createInitialGenerationJob = () => ({
  jobId:    null,
  status:   null,
  progress: 0,
  logs:     [],
});

/**
 * Creates the initial run-job tracking state.
 * Polled after the user starts executing tests.
 *
 * @returns {{ runId: null, status: null, logs: [], results: null }}
 */
const createInitialRunJob = () => ({
  runId:   null,
  status:  null,
  logs:    [],
  results: null,
});

// ─── Combined Factories ───────────────────────────────────────────────────────

/**
 * Creates the full initial `data` slice.
 * Each key corresponds to a domain concept collected during the wizard.
 *
 * @returns {object}
 */
export const createInitialData = () => ({
  sessionId:        null,
  workspace:        createInitialWorkspace(),
  project:          createInitialProject(),
  testSource:       createInitialTestSource(),
  testCreationMode: null, // Set in step 5; one of TEST_CREATION_MODES

  // Feature-gated sub-states (only relevant for specific app types / modes)
  uiMap:       createInitialUiMap(),
  recorder:    createInitialRecorder(),
  uiDiscovery: createInitialUiDiscovery(),
  iot:         createInitialIot(),
  migrate:     createInitialMigrate(),

  // Shared across most flows
  testCases:       [],
  automationPrefs: createInitialAutomationPrefs(),
  generationJob:   createInitialGenerationJob(),
  runJob:          createInitialRunJob(),
});

/**
 * Creates the complete initial wizard state (engine + data).
 * Use this for first-load and after `resetOnboarding()`.
 *
 * @returns {{ engine: object, data: object }}
 */
export const createInitialState = () => ({
  engine: createInitialEngine(),
  data:   createInitialData(),
});